import { twMerge } from "tailwind-merge";

import type { StartedFeatureName } from "@/api/splashpages";
import { Badge } from "@/design-system/components/Badge/Badge";

import { useNavNewBadge } from "./hooks/useNavNewBadge";

export interface NavNewBadgeProps {
  // Identity of the feature this badge belongs to. Matched against the
  // `started_features` records so the badge clears once the user clicks
  // "Get Started". Omit for a badge with no interaction gate (time gate only).
  featureKey?: StartedFeatureName;
  // UTC `Date` marking when the feature became "new", built from an explicit
  // ISO-8601 string with a `Z` offset (e.g. `new Date("2026-06-17T00:00:00Z")`)
  // so the cutoff is unambiguous. Omit (or backdate) to retire the badge with
  // no other code changes.
  newSince?: Date;
  // Days the badge stays visible after `newSince` before the time gate closes.
  ttlDays?: number;
  className?: string;
  variant?: keyof typeof variantSoftStyles;
}

const variantSoftStyles = {
  success: "bg-status-success-tint text-status-success",
  warning: "bg-status-warning-tint text-status-warning",
  error: "bg-status-error-tint text-status-error",
  unknown: "bg-surface-hover text-textSubtle",
  info: "bg-status-info-tint text-status-info",
};

export function NavNewBadge({
  featureKey,
  newSince,
  ttlDays,
  className,
  variant = "success",
}: NavNewBadgeProps) {
  const visible = useNavNewBadge(featureKey, newSince, ttlDays);

  if (!visible) return null;

  return (
    <Badge
      text="New"
      className={twMerge(className, "mr-1", variantSoftStyles[variant])}
    />
  );
}
